import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Shield, Lock, Laptop, Server, Users, Clock, Check, ArrowRight, Globe, Wifi } from "lucide-react";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};

const stagger = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.08 } },
};

const features = [
  {
    icon: Lock,
    title: "Zero-trust access",
    description: "Every device and user is verified before reaching company resources. No implicit trust, regardless of network location."
  },
  {
    icon: Wifi,
    title: "Public Wi-Fi protection",
    description: "Employees work safely from coffee shops, airports, and hotels. Encrypted connections prevent eavesdropping and man-in-the-middle attacks."
  },
  {
    icon: Server,
    title: "Secure cloud access",
    description: "Connect remote workers to AWS, Azure, Google Cloud, and on-premise resources through encrypted tunnels."
  },
  {
    icon: Laptop,
    title: "BYOD support",
    description: "Secure bring-your-own-device policies with per-device VPN profiles. Works on Windows, Mac, iOS, Android, and Linux."
  },
  {
    icon: Users,
    title: "Team management",
    description: "A centralized dashboard to onboard and offboard employees, manage device access, and see team connectivity."
  },
  {
    icon: Globe,
    title: "US network",
    description: "3 US nodes in Texas, Virginia, and Dallas give distributed teams a nearby US point to connect from."
  }
];

const useCases = [
  "Work-from-home employee security",
  "Secure access to company resources",
  "Video conferencing privacy",
  "Cloud application access (SaaS)",
  "International team connectivity",
  "Contractor and vendor access",
  "Company-wide device management",
  "Remote IT administration"
];

const benefits = [
  { title: "Productivity", desc: "A light footprint keeps your team moving" },
  { title: "Security", desc: "Encrypted connections everywhere" },
  { title: "Simplicity", desc: "One-click connect, minimal IT overhead" },
  { title: "Scalability", desc: "Add team members in seconds" }
];

export default function RemoteTeamsVPN() {
  return (
    <>
      <Helmet>
        <title>VPN for Remote Teams & Work From Home | Business Remote Access | SACVPN</title>
        <meta
          name="description"
          content="Secure VPN for remote teams and distributed workforces. Zero-trust access, public Wi-Fi protection, and easy team management for work-from-home employees."
        />
        <meta
          name="keywords"
          content="remote team VPN, work from home VPN, remote work security, distributed team VPN, remote access VPN, business VPN, team VPN management, WFH security"
        />
        <link rel="canonical" href="https://www.sacvpn.com/industries/remote-teams" />
        <meta property="og:title" content="VPN for Remote Teams & Work From Home | Business Remote Access | SACVPN" />
        <meta property="og:description" content="Secure VPN for remote teams and distributed workforces. Zero-trust access, public Wi-Fi protection, and easy team management for work-from-home employees." />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            "serviceType": "Remote Teams VPN Service",
            "name": "SACVPN Remote Teams VPN Solutions",
            "description": "Secure VPN service for remote teams and distributed workforces, enabling secure work from anywhere.",
            "provider": {
              "@type": "Organization",
              "name": "SACVPN"
            },
            "areaServed": {
              "@type": "Country",
              "name": "United States"
            },
            "audience": {
              "@type": "Audience",
              "audienceType": "Businesses with Remote Workers"
            }
          })}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section className="relative py-24 overflow-hidden border-b border-white/5">
        <div className="absolute inset-0 bg-dark-900" />
        <div className="absolute inset-0 bg-[radial-gradient(900px_460px_at_78%_20%,rgba(14,165,233,0.14),transparent_60%)]" />

        <div className="relative z-10 max-w-7xl mx-auto px-6">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={stagger}
            className="grid lg:grid-cols-2 gap-12 items-center"
          >
            <div>
              <motion.p variants={fadeUp} className="text-xs font-mono uppercase tracking-[0.2em] text-brand-400 mb-4">
                Remote work
              </motion.p>

              <motion.h1
                variants={fadeUp}
                className="text-4xl md:text-5xl lg:text-[3.25rem] font-display font-bold text-white leading-[1.05] tracking-tight"
              >
                Secure VPN for{" "}
                <span className="text-brand-400">remote teams</span>
              </motion.h1>

              <motion.p
                variants={fadeUp}
                className="mt-6 text-lg text-gray-400 leading-relaxed max-w-xl"
              >
                Give your distributed workforce secure access from anywhere. Zero-trust access, public Wi-Fi protection, and simple team management for modern businesses.
              </motion.p>

              <motion.div variants={fadeUp} className="mt-9 flex flex-wrap gap-3">
                <Link
                  to="/pricing"
                  className="inline-flex items-center gap-2 px-6 py-3.5 rounded-lg bg-brand-500 text-white font-semibold hover:bg-brand-400 transition-colors"
                >
                  <Clock className="w-5 h-5" />
                  Start free trial
                </Link>
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 px-6 py-3.5 rounded-lg border border-white/15 text-white font-semibold hover:bg-white/5 transition-colors"
                >
                  Request demo
                </Link>
              </motion.div>
            </div>

            <motion.div variants={fadeUp} className="relative">
              <div className="rounded-xl border border-white/[0.08] bg-dark-800/40 p-8">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-11 h-11 rounded-lg bg-brand-500/10 border border-brand-500/20 flex items-center justify-center">
                    <Users className="w-5 h-5 text-brand-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white">Team security</h3>
                    <p className="text-gray-500 text-sm">Work anywhere, securely</p>
                  </div>
                </div>
                <div className="space-y-3">
                  {["Zero-trust access", "One-click connect", "Cross-platform support", "Centralized management"].map((item, i) => (
                    <div key={i} className="flex items-center gap-3 text-gray-400">
                      <Check className="w-4 h-4 text-brand-400 flex-shrink-0" />
                      <span className="text-sm">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-24 bg-dark-900">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            className="max-w-2xl mb-14"
          >
            <p className="text-xs font-mono uppercase tracking-[0.2em] text-brand-400 mb-4">Why teams choose us</p>
            <h2 className="text-3xl md:text-4xl font-display font-bold text-white tracking-tight">
              Security that enables productivity
            </h2>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={stagger}
            className="grid md:grid-cols-2 lg:grid-cols-4 gap-6"
          >
            {benefits.map((point, i) => (
              <motion.div
                key={i}
                variants={fadeUp}
                className="rounded-xl border border-white/[0.08] bg-dark-900 p-6 hover:border-brand-500/30 transition-colors"
              >
                <div className="w-11 h-11 rounded-lg bg-brand-500/10 border border-brand-500/20 flex items-center justify-center mb-5">
                  <Check className="w-5 h-5 text-brand-400" />
                </div>
                <h3 className="font-semibold text-white mb-2">{point.title}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{point.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 bg-dark-800/40 border-y border-white/5">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            className="max-w-2xl mb-14"
          >
            <p className="text-xs font-mono uppercase tracking-[0.2em] text-brand-400 mb-4">Capabilities</p>
            <h2 className="text-3xl md:text-4xl font-display font-bold text-white tracking-tight">
              Built for distributed workforces
            </h2>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={stagger}
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/[0.06] border border-white/[0.06] rounded-xl overflow-hidden"
          >
            {features.map((feature, i) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={i}
                  variants={fadeUp}
                  className="bg-dark-900 p-8 hover:bg-dark-800 transition-colors"
                >
                  <div className="w-11 h-11 rounded-lg bg-brand-500/10 border border-brand-500/20 flex items-center justify-center mb-5">
                    <Icon className="w-5 h-5 text-brand-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-white mb-2">{feature.title}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{feature.description}</p>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section className="py-24 bg-dark-900">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={stagger}
            >
              <motion.p variants={fadeUp} className="text-xs font-mono uppercase tracking-[0.2em] text-brand-400 mb-4">
                Use cases
              </motion.p>
              <motion.h2
                variants={fadeUp}
                className="text-3xl md:text-4xl font-display font-bold text-white tracking-tight mb-6"
              >
                Remote work use cases
              </motion.h2>
              <motion.p
                variants={fadeUp}
                className="text-lg text-gray-400 mb-8 leading-relaxed"
              >
                From home offices to coffee shops, SACVPN keeps your team connected and secure. For a deeper look
                at the risks and safeguards, read our{" "}
                <Link to="/blog/vpn-for-remote-work-security" className="text-brand-400 underline decoration-brand-500/40 underline-offset-2 hover:text-brand-300 transition-colors">
                  VPN for remote work security guide
                </Link>.
              </motion.p>
              <motion.div
                variants={fadeUp}
                className="grid grid-cols-1 sm:grid-cols-2 gap-4"
              >
                {useCases.map((useCase, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-brand-400 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-400">{useCase}</span>
                  </div>
                ))}
              </motion.div>
            </motion.div>

            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeUp}
              className="rounded-xl border border-white/[0.08] bg-dark-800/40 p-8"
            >
              <h3 className="text-xl font-semibold text-white mb-6">Get started in minutes</h3>
              <div className="space-y-4">
                {[
                  "1. Sign up and create your team",
                  "2. Invite team members via email",
                  "3. Employees install and connect",
                  "4. Manage access from your dashboard",
                  "5. See team connectivity at a glance"
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3 text-gray-400">
                    <ArrowRight className="w-5 h-5 text-brand-400" />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-dark-900 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            className="relative overflow-hidden rounded-2xl border border-brand-500/20 bg-[radial-gradient(700px_320px_at_50%_-10%,rgba(14,165,233,0.18),transparent_70%)] bg-dark-800 px-8 md:px-14 py-16 text-center"
          >
            <h2 className="text-3xl md:text-4xl font-display font-bold text-white tracking-tight mb-4">
              Secure your remote workforce
            </h2>
            <p className="text-lg text-gray-400 max-w-2xl mx-auto mb-9">
              Give your team encrypted access from anywhere. Start your 14-day free trial.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link
                to="/pricing"
                className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-lg bg-brand-500 text-white font-semibold hover:bg-brand-400 transition-colors"
              >
                <Clock className="w-5 h-5" />
                Start free trial
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-lg border border-white/15 text-white font-semibold hover:bg-white/5 transition-colors"
              >
                Contact sales
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
}
